import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LinearProgress } from '@mui/joy';
import { useDataProvider } from "src/mtska-frontend-data-provider";
import { useTranslation } from 'react-i18next';
import { useFormatter } from "src/mtska-frontend-app-component/utils";
import { useDashboardPrintRender } from 'src/mtska-frontend-modules/modules/_shared/dashboardConfigured/DashboardPrintRenderContext';
import { bindPivotScrollToLatest } from 'src/mtska-frontend-modules/modules/_shared/dashboardConfigured/scrollPivotTablesToLatest';

/** Legge un campo da una cella pivot: numero legacy o oggetto { field: n, _id?: … } */
function getPivotCellFieldRaw(raw, fieldKey) {
    if (raw == null || raw === "") {
        return null;
    }
    if (typeof raw === "number") {
        return Number.isNaN(raw) ? null : raw;
    }
    if (typeof raw === "object" && !Array.isArray(raw) && fieldKey != null) {
        if (Object.prototype.hasOwnProperty.call(raw, fieldKey) && raw[fieldKey] != null) {
            const n = Number(raw[fieldKey]);
            return Number.isNaN(n) ? null : n;
        }
    }
    return null;
}

/** Legacy: cella come numero puro o oggetto con una sola metrica “non _id” */
function getPivotCellLegacyNumber(raw) {
    if (raw == null || raw === "") {
        return null;
    }
    if (typeof raw === "number") {
        return Number.isNaN(raw) ? null : raw;
    }
    if (typeof raw === "object" && !Array.isArray(raw)) {
        for (const k of Object.keys(raw)) {
            if (k === "_id") continue;
            const n = Number(raw[k]);
            if (!Number.isNaN(n)) {
                return n;
            }
        }
    }
    return null;
}

function pivotHeatValue(raw, heatmapField) {
    if (heatmapField == null) return 0;
    return getPivotCellFieldRaw(raw, heatmapField) ?? 0;
}

function specForField(valueSpecs, fieldKey) {
    return valueSpecs.find((s) => s.field === fieldKey);
}

const PIVOT_ROW_LABEL_COL_WIDTH = 180;
const PIVOT_TOTAL_COL_WIDTH = 90;
const PIVOT_STICKY_LEFT_CLASS = 'mtska-pivot-sticky-left';
const PIVOT_STICKY_RIGHT_CLASS = 'mtska-pivot-sticky-right';
const PRINT_MAX_MONTH_COLUMNS = 6;

const pivotStickyLeftStyle = (index) => ({
    '--pivot-sticky-left': `${index * PIVOT_ROW_LABEL_COL_WIDTH}px`,
});

const pivotStickyRightStyle = (indexFromRight) => ({
    '--pivot-sticky-right': `${indexFromRight * PIVOT_TOTAL_COL_WIDTH}px`,
});

const mergePivotCellStyles = (...styles) => Object.assign({}, ...styles.filter(Boolean));

const PivotTable = ({
    dataProviderName,
    filters,
    compareFilters = null,
    rows = [],
    values = [],
    columns = [],
    format = "number"
}) => {
    const { t } = useTranslation();
    const { formatNumber, formatCurrency, formatPercentage } = useFormatter();
    const printMode = useDashboardPrintRender();

    const rowDepthCount = rows.length;
    const fullyExpandedDepth = rowDepthCount > 0 ? rowDepthCount - 1 : 0;

    const valueSpecs = useMemo(
        () => (values || []).filter((v) => v?.field != null && v.field !== ""),
        [values]
    );
    const valueFields = useMemo(() => valueSpecs.map((s) => s.field), [valueSpecs]);

    /** Heatmap sulla prima metrica “non numero” (es. valuta); altrimenti prima diversa da _id */
    const heatmapMetricField = useMemo(() => {
        for (const spec of valueSpecs) {
            const mf = spec.format ?? format;
            if (mf === "currency" || mf === "percent" || mf === "percentage" || mf === "percentFloat") {
                return spec.field;
            }
        }
        const nonId = valueFields.find((f) => f !== "_id");
        return nonId ?? valueFields[0];
    }, [valueSpecs, valueFields, format]);

    const multiMetrics = valueFields.length > 1;
    const theadRowSpan = multiMetrics ? 2 : 1;

    const formatMetricValue = useCallback((fieldKey, v) => {
        const spec = specForField(valueSpecs, fieldKey);
        const metricFormat = spec?.format ?? format;
        if (v == null || Number.isNaN(Number(v))) {
            return "–";
        }
        const num = Number(v);
        switch (metricFormat) {
            case "currency":
                return formatCurrency(
                    num,
                    spec?.compactformat ?? false,
                    spec?.decimaldigits ?? "full"
                );
            case "percent":
            case "percentage":
                return formatPercentage(num);
            case "percentFloat":
                return formatPercentage(num, true);
            case "number":
            default:
                return num === 0 ? "–" : formatNumber(num);
        }
    }, [valueSpecs, format, formatNumber, formatCurrency, formatPercentage]);

    const useHeatmap = (data, heatField) => {
        return useMemo(() => {
            if (!data || !data.columns || heatField == null) return {};
            const maxByColumn = {};
            data.columns.forEach((col) => {
                let max = 0;
                (data.rows || []).forEach((row) => {
                    const children = row?.children || [];
                    children.forEach((child) => {
                        const v = pivotHeatValue(child.values?.[col], heatField);
                        if (v > max) max = v;
                    });
                    const groupValue = pivotHeatValue(row.values?.[col], heatField);
                    if (groupValue > max) max = groupValue;
                });
                maxByColumn[col] = max;
            });
            return maxByColumn;
        }, [data, heatField]);
    };

    const [drawable, setDrawable] = useState();
    const [data, setData] = useState({});
    const [knownFilters, setKnownFilters] = useState();
    const [maxVisibleDepth, setMaxVisibleDepth] = useState(0);
    const effectiveMaxVisibleDepth =
        printMode && rowDepthCount > 0 ? fullyExpandedDepth : maxVisibleDepth;

    const { items, loadAll, reloadCounter } = useDataProvider(dataProviderName);

    useEffect(() => {
        const shouldReload = (JSON.stringify(filters) != knownFilters) || (typeof filters == typeof undefined && items.length == 0);
        if (shouldReload) {
            setKnownFilters(JSON.stringify(filters));
            loadAll(filters, null, null, null, {
                rows,
                values,
                columns,
                compareFilters
            });
        }
    }, [reloadCounter, filters, rows, values, columns, compareFilters]);

    useEffect(() => {
        if (!items) return;

        if (Array.isArray(items)) return;
        setData(items);
        setDrawable(true);
    }, [items]);

    const maxByColumn = useHeatmap(data, heatmapMetricField);
    const { columns: dataColumns = [], rows: dataRows = [], grandTotal = { values: {}, total: 0 } } = data;
    const displayColumns = useMemo(() => {
        if (!printMode || dataColumns.length <= PRINT_MAX_MONTH_COLUMNS) {
            return dataColumns;
        }
        return dataColumns.slice(-PRINT_MAX_MONTH_COLUMNS);
    }, [printMode, dataColumns]);
    const isPrintColumnSlice = printMode && displayColumns.length < dataColumns.length;

    const metricHeaderLabel = (spec) => t(spec.label ?? spec.field);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        if (!printMode || !drawable || isPrintColumnSlice) {
            return undefined;
        }

        return bindPivotScrollToLatest(scrollContainerRef.current);
    }, [printMode, drawable, isPrintColumnSlice, displayColumns.length, dataRows.length, effectiveMaxVisibleDepth, multiMetrics]);

    return (!drawable ? <LinearProgress /> :
        <div className={`mtska-pivot-wrapper${printMode ? ' mtska-pivot-wrapper--print' : ''}`}>
            <div className="mtska-pivot-scroll" ref={scrollContainerRef}>
                <table className="mtska-pivot-table">
                    <thead className="mtska-pivot-thead">
                        <tr className="mtska-pivot-thead__row">
                            {rows
                                .filter((_, idx) => idx <= effectiveMaxVisibleDepth)
                                .map((r, idx) => {
                                    const isGroupCol = idx === 0;
                                    const originalIdx = idx;
                                    const canToggleNext = originalIdx < rows.length - 1;
                                    const nextDepth = originalIdx + 1;
                                    const isExpanded = effectiveMaxVisibleDepth >= nextDepth;

                                    return (
                                        <th
                                            key={r.field}
                                            rowSpan={theadRowSpan}
                                            className={`${isGroupCol
                                                ? "mtska-pivot-thead__cell mtska-pivot-thead__cell--group"
                                                : "mtska-pivot-thead__cell mtska-pivot-thead__cell--leaf"} ${PIVOT_STICKY_LEFT_CLASS}`}
                                            style={pivotStickyLeftStyle(idx)}
                                        >
                                            {t(r.field)}
                                            {canToggleNext && !printMode && (
                                                <button
                                                    className={`mtska-pivot-toggle ${isExpanded ? "" : "mtska-pivot-toggle--collapsed"}`}
                                                    onClick={() =>
                                                        setMaxVisibleDepth(prev =>
                                                            prev >= nextDepth ? originalIdx : nextDepth
                                                        )
                                                    }
                                                    title={
                                                        isExpanded
                                                            ? `Comprimi ${rows[nextDepth]?.field ?? ""}`
                                                            : `Espandi ${rows[nextDepth]?.field ?? ""}`
                                                    }
                                                >
                                                    <span className="mtska-pivot-toggle__icon">▾</span>
                                                </button>
                                            )}
                                        </th>
                                    );
                                })}
                            {displayColumns.map((col) => (
                                <th
                                    key={col}
                                    colSpan={multiMetrics ? valueFields.length : 1}
                                    className="mtska-pivot-thead__cell mtska-pivot-thead__cell--month"
                                >
                                    {t(col)}
                                </th>
                            ))}
                            <th
                                colSpan={multiMetrics ? valueFields.length : 1}
                                className={`mtska-pivot-thead__cell mtska-pivot-thead__cell--total ${PIVOT_STICKY_RIGHT_CLASS}`}
                                style={pivotStickyRightStyle(0)}
                            >
                                Totale
                            </th>
                        </tr>
                        {multiMetrics && (
                            <tr className="mtska-pivot-thead__row mtska-pivot-thead__row--metrics">
                                {displayColumns.flatMap((col) =>
                                    valueSpecs.map((spec) => (
                                        <th
                                            key={`${col}::${spec.field}`}
                                            className="mtska-pivot-thead__cell mtska-pivot-thead__cell--month mtska-pivot-thead__cell--metric"
                                        >
                                            {metricHeaderLabel(spec)}
                                        </th>
                                    ))
                                )}
                                {valueSpecs.map((spec, specIdx) => (
                                    <th
                                        key={`total::${spec.field}`}
                                        className={`mtska-pivot-thead__cell mtska-pivot-thead__cell--total mtska-pivot-thead__cell--metric ${PIVOT_STICKY_RIGHT_CLASS}`}
                                        style={pivotStickyRightStyle(valueSpecs.length - 1 - specIdx)}
                                    >
                                        {metricHeaderLabel(spec)}
                                    </th>
                                ))}
                            </tr>
                        )}
                    </thead>

                    <tbody className="mtska-pivot-tbody">
                        {dataRows.map((row, rowIdx) => (
                            <PartnerRows
                                key={row.label ?? row.partner ?? String(rowIdx)}
                                row={row}
                                rowsMeta={rows}
                                columns={displayColumns}
                                maxByColumn={maxByColumn}
                                maxVisibleDepth={effectiveMaxVisibleDepth}
                                valueFields={valueFields}
                                multiMetrics={multiMetrics}
                                heatmapMetricField={heatmapMetricField}
                                formatMetricValue={formatMetricValue}
                            />
                        ))}
                    </tbody>

                    <tfoot className="mtska-pivot-tfoot">
                        <tr className="mtska-pivot-tfoot__row">
                            <td
                                className={`mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--label ${PIVOT_STICKY_LEFT_CLASS}`}
                                style={pivotStickyLeftStyle(0)}
                                colSpan={Math.min(rows.length, effectiveMaxVisibleDepth + 1)}
                            >
                                Totale complessivo
                            </td>
                            {displayColumns.flatMap((col) =>
                                buildPivotFooterCells({
                                    rawCell: grandTotal.values[col],
                                    valueSpecs,
                                    valueFields,
                                    multiMetrics,
                                    colKey: col,
                                    formatMetricValue,
                                    isGrandTotalColumn: false
                                })
                            )}
                            {buildPivotFooterCells({
                                rawCell: grandTotal.total,
                                valueSpecs,
                                valueFields,
                                multiMetrics,
                                colKey: "__grandTotal__",
                                formatMetricValue,
                                isGrandTotalColumn: true
                            })}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

function buildPivotFooterCells({
    rawCell,
    valueSpecs,
    valueFields,
    multiMetrics,
    colKey,
    formatMetricValue,
    isGrandTotalColumn
}) {
    const baseClass = isGrandTotalColumn
        ? "mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--total"
        : "mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--value";

    if (!valueFields.length) {
        return [
            <td key={`${colKey}::empty`} className={baseClass}>–</td>
        ];
    }

    if (!multiMetrics) {
        const fk = valueFields[0];
        const v = getPivotCellFieldRaw(rawCell, fk) ?? getPivotCellLegacyNumber(rawCell);
        return [
            <td
                key={`${colKey}::${fk}`}
                className={`${baseClass}${isGrandTotalColumn ? ` ${PIVOT_STICKY_RIGHT_CLASS}` : ''}`}
                style={isGrandTotalColumn ? pivotStickyRightStyle(0) : undefined}
            >
                {formatMetricValue(fk, v)}
            </td>
        ];
    }

    return valueFields.map((fk, metricIdx) => {
        const v = getPivotCellFieldRaw(rawCell, fk);
        return (
            <td
                key={`${colKey}::${fk}`}
                className={`${baseClass}${isGrandTotalColumn ? ` ${PIVOT_STICKY_RIGHT_CLASS}` : ''}`}
                style={isGrandTotalColumn ? pivotStickyRightStyle(valueFields.length - 1 - metricIdx) : undefined}
            >
                {formatMetricValue(fk, v)}
            </td>
        );
    });
}

/**
 * Righe di dettaglio compatte: niente riga solo-genitore con metriche duplicate;
 * primo figlio sulla stessa riga del genitore (valori dal primo figlio); `rowLabel`
 * in colonna 0 solo sulla prima riga del blocco (se dimCount > 1).
 *
 * Con una sola colonna dimensione visibile (maxVisibleDepth 0), i figli di `row`
 * appartengono a livelli nascosti: come l’ex filtro depth <= maxVisibleDepth, non
 * emettiamo righe di dettaglio (resta solo la subtotal con row.label in colonna 0).
 */
function buildCompactDetailRows(children, rowLabel, dimCount, maxVisibleDepth) {
    if (!children?.length || dimCount < 2) {
        return [];
    }
    /** Colonna 0 = valore gruppo (row.label sulla prima riga); dati gerarchici da col 1 */
    const firstDataCol = 1;
    const emptyPrefix = () => Array(dimCount).fill("");

    function expand(node, colIndex, prefix, keyPrefixParts) {
        const hasKids = Array.isArray(node.children) && node.children.length > 0;
        const canExpand = hasKids && colIndex < maxVisibleDepth;

        if (!canExpand) {
            const labels = prefix.slice();
            labels[colIndex] = node.label ?? "";
            const rowKey = [...keyPrefixParts, node.label ?? ""].join(">");
            return [{ labels, dataNode: node, rowKey }];
        }

        const [firstChild, ...siblings] = node.children;
        const firstPrefix = prefix.slice();
        firstPrefix[colIndex] = node.label ?? "";
        const parentKey = [...keyPrefixParts, node.label ?? ""];

        const rows = [];
        rows.push(...expand(firstChild, colIndex + 1, firstPrefix, parentKey));
        for (const sib of siblings) {
            rows.push(...expand(sib, colIndex + 1, emptyPrefix(), parentKey));
        }
        return rows;
    }

    const all = [];
    for (const child of children) {
        all.push(...expand(child, firstDataCol, emptyPrefix(), []));
    }

    if (all.length > 0 && dimCount > 1) {
        const first = all[0];
        first.labels = first.labels.slice();
        first.labels[0] = rowLabel ?? "";
    }

    return all;
}

function PartnerRows({
    row,
    rowsMeta,
    columns,
    maxByColumn,
    maxVisibleDepth,
    valueFields,
    multiMetrics,
    heatmapMetricField,
    formatMetricValue
}) {
    const children = row?.children || [];

    const visibleRowsMeta = rowsMeta.filter((_, idx) => idx <= maxVisibleDepth);
    const dimCount = visibleRowsMeta.length;
    const visibleDetailRows = buildCompactDetailRows(
        children,
        row?.label,
        dimCount,
        maxVisibleDepth
    );

    /** Evita di ripetere row.label in subtotal se già mostrato sulla prima riga di dettaglio */
    const skipSubtotalGroupLabel = dimCount > 1 && visibleDetailRows.length > 0;

    const renderMetricCells = (rawCell, col, cellClassName, stickyTotal = false) => {
        if (!valueFields.length) {
            return [
                <td key={`${col}::empty`} className={cellClassName}>–</td>
            ];
        }
        if (!multiMetrics) {
            const fk = valueFields[0];
            const v = getPivotCellFieldRaw(rawCell, fk) ?? getPivotCellLegacyNumber(rawCell);
            const heatNum = pivotHeatValue(rawCell, heatmapMetricField);
            const maxH = maxByColumn[col] ?? 0;
            const intensity = maxH > 0 ? heatNum / maxH : 0;
            const showHeat = heatmapMetricField === fk && heatNum > 0 && maxH > 0;
            return [
                <td
                    key={`${col}::${fk}`}
                    className={`${cellClassName}${stickyTotal ? ` ${PIVOT_STICKY_RIGHT_CLASS}` : ''}`}
                    style={mergePivotCellStyles(
                        showHeat ? { "--heat-intensity": intensity.toFixed(3) } : undefined,
                        stickyTotal ? pivotStickyRightStyle(0) : undefined,
                    )}
                    data-heat={showHeat ? "true" : undefined}
                >
                    {formatMetricValue(fk, v)}
                </td>
            ];
        }
        return valueFields.map((fk, metricIdx) => {
            const v = getPivotCellFieldRaw(rawCell, fk);
            const heatNum = fk === heatmapMetricField ? (v ?? 0) : 0;
            const maxH = maxByColumn[col] ?? 0;
            const intensity = maxH > 0 ? heatNum / maxH : 0;
            const showHeat = fk === heatmapMetricField && heatNum > 0 && maxH > 0;
            return (
                <td
                    key={`${col}::${fk}`}
                    className={`${cellClassName}${stickyTotal ? ` ${PIVOT_STICKY_RIGHT_CLASS}` : ''}`}
                    style={mergePivotCellStyles(
                        showHeat ? { "--heat-intensity": intensity.toFixed(3) } : undefined,
                        stickyTotal ? pivotStickyRightStyle(valueFields.length - 1 - metricIdx) : undefined,
                    )}
                    data-heat={showHeat ? "true" : undefined}
                >
                    {formatMetricValue(fk, v)}
                </td>
            );
        });
    };

    return (
        <>
            {visibleDetailRows.map(({ labels, dataNode, rowKey }, idx) => (
                    <tr
                        key={`${row.label ?? ""}__${rowKey}`}
                        className={`mtska-pivot-tbody__row mtska-pivot-tbody__row--area ${idx === 0 ? "mtska-pivot-tbody__row--first" : ""}`}
                    >
                        {visibleRowsMeta.map((_, colIdx) => {
                            const isGroupCol = colIdx === 0;
                            const className = isGroupCol
                                ? "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--group"
                                : "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--leaf";
                            return (
                                <td
                                    key={colIdx}
                                    className={`${className} ${PIVOT_STICKY_LEFT_CLASS}`}
                                    style={pivotStickyLeftStyle(colIdx)}
                                >
                                    {labels[colIdx] ?? ""}
                                </td>
                            );
                        })}

                        {columns.flatMap((col) =>
                            renderMetricCells(
                                dataNode.values?.[col],
                                col,
                                "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--value"
                            )
                        )}

                        {renderMetricCells(
                            dataNode.total,
                            "__rowtotal__",
                            "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--rowtotal",
                            true
                        )}
                    </tr>
                ))}

            <tr className="mtska-pivot-tbody__row mtska-pivot-tbody__row--subtotal">
                {visibleRowsMeta.map((_, colIdx) => {
                    const isGroupCol = colIdx === 0;
                    const className = isGroupCol
                        ? "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--group"
                        : "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-label";
                    return (
                        <td
                            key={colIdx}
                            className={`${className} ${PIVOT_STICKY_LEFT_CLASS}`}
                            style={pivotStickyLeftStyle(colIdx)}
                        >
                            {isGroupCol
                                ? skipSubtotalGroupLabel
                                    ? ""
                                    : row.label
                                : colIdx === 1
                                  ? "Totale"
                                  : ""}
                        </td>
                    );
                })}
                {columns.flatMap((col) =>
                    renderMetricCells(row.values?.[col], col, "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-value")
                )}
                {renderMetricCells(row.total, "__subtotal__", "mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-total", true)}
            </tr>
        </>
    );
}

export default PivotTable;
